/**
 * Recent runs, as a card — used on the job page and the schedule page.
 *
 * Each row is one fire (its attempts folded together) and opens that run's own
 * page, so "what did this job do?" is answered in place rather than by switching
 * to the run history grid and filtering it back down to the same job.
 */
import { Tag } from 'sharedUi/Tag';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import type { RunGroup } from '../../types/background-job.types';
import { CARD, CardHeader } from '../../components/notifications/engine-view-ui';
import { RUN_STATUS_SEVERITY, latestAttempt, runDuration } from './run-format';
import { runPath } from './view-paths';

export const RunsCard = ({ title, hint, groups, loading, empty }: {
  title: string;
  hint?: string;
  groups: RunGroup[];
  loading?: boolean;
  empty: string;
}) => {
  const navigate = useAppNavigate();

  return (
    <div className={CARD}>
      <CardHeader icon="history" color="text-emerald-500" title={title} hint={hint} />
      {loading ? (
        <div className="px-4 py-4 text-sm text-gray-400">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="px-4 py-4 text-sm text-gray-400">{empty}</div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {groups.map((g) => {
            const last = latestAttempt(g);
            const manual = g.triggeredBy === 'MANUAL_USER';
            return (
              <li key={g.runCorrelationId}>
                <button
                  type="button"
                  onClick={() => navigate(runPath(last._id))}
                  className="flex w-full flex-wrap items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  data-testid={`RunsCard.Run.${last._id}`}
                >
                  <span className="w-40 shrink-0 text-xs text-gray-600 dark:text-gray-300">
                    {g.startedAt ? formatTimestamp(g.startedAt) : '—'}
                  </span>
                  <Tag value={g.status} severity={RUN_STATUS_SEVERITY[g.status]} />
                  {g.attempts.length > 1 && <Tag value={`${g.attempts.length} attempts`} severity="warning" />}
                  <Tag value={manual ? 'Manual' : 'Scheduler'} severity={manual ? 'info' : 'secondary'} />
                  {g.jobName && <span className="text-sm text-ink">{g.jobName}</span>}
                  <span className="ml-auto text-xs text-ink-muted">{runDuration(g)}</span>
                  <i className="pi pi-chevron-right text-xs text-gray-400" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RunsCard;
